import { newController, serializeController } from '../components/animator/controller-doc.js';
import { MIXER_FORMAT, serializeMixer } from '../components/audio/mixer-doc.js';
import { newUiDoc, serializeUiDoc } from '../components/ui-editor/ui-doc.js';
import { newProcgenDoc } from '../components/procgen/procgen-doc.js';
import { newVideoDoc } from '@ai-studio/shared';

import type { IconName } from '../components/ui/icons.js';
import { newModel, serializeModel } from './model-doc.js';
import { newPixelDoc, serializePixelDoc } from './pixel-doc.js';
import { emptyScene, serializeScene, type SceneDoc } from './scene-doc.js';
import { newSoundDoc, serializeSound } from './sound-doc.js';

/**
 * Assets → Create: starter documents for each asset type (Unity's
 * Assets/Create menu). Each new file opens in its editor afterwards.
 */

export interface CreatableAsset {
  id: string;
  label: string;
  icon: IconName;
  /** File ending. */
  suffix: string;
  /** Suggested file name (without the ending). */
  defaultName: string;
  content: (name: string) => string;
}

const json = (value: unknown) => `${JSON.stringify(value, null, 2)}\n`;

/** A lit 3D starting point: camera, sun and ground (Unity's default scene). */
export function basicScene(name: string): SceneDoc {
  return {
    ...emptyScene(name),
    entities: [
      { id: 'main_camera', name: 'Main Camera', transform: { position: [0, 3, 8], rotation: [-15, 0, 0] }, components: { camera: { primary: true, fov: 60 } } },
      { id: 'sun', name: 'Directional Light', transform: { rotation: [-50, -30, 0] }, components: { light: { kind: 'directional', color: [1, 0.96, 0.9], intensity: 2, shadows: 'soft' } } },
      {
        id: 'ground',
        name: 'Ground',
        transform: { scale: [20, 1, 20] },
        components: { mesh: { source: 'primitive:plane' }, collider: { shape: 'box', size: [1, 0.02, 1] } },
      },
    ],
  };
}

export const SCRIPT_TEMPLATE = (name: string) => `// ${name}: attached to an entity's Script component; \`this\` is the entity, \`props\` its props.

fn on_start() {
}

fn on_update(dt) {
}
`;

export const CREATABLE: CreatableAsset[] = [
  {
    id: 'material',
    label: 'Material',
    icon: 'material',
    suffix: '.material.json',
    defaultName: 'New Material',
    content: (name) => json({ format: 'prism.material/1', name, shader: 'standard', color: [0.8, 0.8, 0.8, 1], metallic: 0, roughness: 0.6 }),
  },
  {
    id: 'physicsMaterial',
    label: 'Physics Material',
    icon: 'sphere',
    suffix: '.physicsmat.json',
    defaultName: 'New Physics Material',
    content: (name) =>
      json({ format: 'prism.physicsmat/1', name, dynamicFriction: 0.6, staticFriction: 0.6, bounciness: 0, frictionCombine: 'average', bounceCombine: 'average' }),
  },
  {
    id: 'prefab',
    label: 'Prefab',
    icon: 'prefab',
    suffix: '.prefab.json',
    defaultName: 'New Prefab',
    content: (name) =>
      json({ format: 'prism.prefab/1', name, entities: [{ id: 'root', name, transform: { position: [0, 0, 0] }, components: { mesh: { source: 'primitive:cube' } } }] }),
  },
  { id: 'script', label: 'Script', icon: 'script', suffix: '.rhai', defaultName: 'new_script', content: SCRIPT_TEMPLATE },
  {
    id: 'controller',
    label: 'Animator Controller',
    icon: 'play',
    suffix: '.controller.json',
    defaultName: 'New Animator Controller',
    content: (name) => serializeController({ ...newController(), name }),
  },
  { id: 'ui', label: 'UI Document', icon: 'ui', suffix: '.ui.json', defaultName: 'New UI', content: (name) => serializeUiDoc(newUiDoc(name)) },
  {
    id: 'uistyle',
    label: 'UI Style Sheet',
    icon: 'ui',
    suffix: '.uistyle.json',
    defaultName: 'theme',
    // A small readable starting theme: every class / state is listed in docs/systems/ui.md.
    content: () =>
      json({
        format: 'prism.uistyle/1',
        fonts: {},
        classes: {
          '*': { textColor: '#e8ecf2', textSize: 24 },
          panel: { backgroundColor: '#14161cE6', cornerRadius: 10 },
          button: {
            backgroundColor: '#2d6cdf',
            cornerRadius: 8,
            textAlign: 'middleCenter',
            hover: { backgroundColor: '#3b7bf0' },
            pressed: { backgroundColor: '#1f55b8' },
            focused: { borderColor: '#ffffff', borderWidth: 2 },
            disabled: { opacity: 0.4 },
            transition: 0.1,
          },
          title: { textSize: 48 },
        },
      }),
  },
  {
    id: 'mixer',
    label: 'Audio Mixer',
    icon: 'audio',
    suffix: '.mixer.json',
    defaultName: 'New Audio Mixer',
    content: () => serializeMixer({ format: MIXER_FORMAT, buses: [{ name: 'Master', volumeDb: 0 }, { name: 'Music' }, { name: 'SFX' }] }),
  },
  { id: 'sound', label: 'Sound', icon: 'audio', suffix: '.sound.json', defaultName: 'New Sound', content: (name) => serializeSound(newSoundDoc(name)) },
  { id: 'model', label: 'Model', icon: 'model', suffix: '.model.json', defaultName: 'New Model', content: (name) => serializeModel(newModel(name)) },
  { id: 'pixel', label: 'Pixel Art', icon: 'pixel', suffix: '.pixel.json', defaultName: 'New Sprite', content: () => serializePixelDoc(newPixelDoc()) },
  { id: 'video', label: 'Video (storyboard)', icon: 'camera', suffix: '.video.json', defaultName: 'New Video', content: (name) => json(newVideoDoc(name)) },
  { id: 'procgen', label: 'Procedural generator', icon: 'layers', suffix: '.procgen.json', defaultName: 'New Generator', content: newProcgenDoc },
];

/** Scene starters offered before the engine's project templates. */
export const SCENE_STARTERS: { id: string; label: string; content: (name: string) => string }[] = [
  { id: 'basic', label: 'Basic 3D (camera, light, ground)', content: (name) => serializeScene(basicScene(name)) },
  { id: 'empty', label: 'Empty', content: (name) => serializeScene(emptyScene(name)) },
];

/** `folder/base<suffix>`, numbered (`base 1`, `base 2`...) until it is free. */
export function uniqueAssetPath(taken: ReadonlySet<string>, folder: string, base: string, suffix: string): string {
  const clean = base.trim().replace(/[\\/:*?"<>|]+/g, '_') || 'New Asset';
  const at = (name: string) => `${folder ? `${folder}/` : ''}${name}${suffix}`;
  const lower = new Set([...taken].map((p) => p.toLowerCase()));
  if (!lower.has(at(clean).toLowerCase())) return at(clean);
  for (let i = 1; ; i++) {
    const candidate = at(`${clean} ${i}`);
    if (!lower.has(candidate.toLowerCase())) return candidate;
  }
}
